import { inject, Injectable } from '@angular/core';

import { EMPTY } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { environment } from '@rocksoft/environment';
import { CategoryApplicationConfigurationService } from '@rocksoft/settings-sdk';
import { compareApplications, safeMap } from '../effects.helpers';
import { ApplicationsApiActions, CategoryApplicationViewActions } from './category-applications.actions';
import { CategoryConfigurationInheritedApiActions } from '../category-configuration-inherited/category-configuration-inherited.actions';

@Injectable()
export class CategoryApplicationsEffects {
  private readonly actions = inject(Actions);
  private readonly categoryApplicationService = inject(CategoryApplicationConfigurationService);

  protected features = environment.features;

  /**
   * The category application configuration has been updated.
   * Effect: we submit the update to the api and dispatch an error if applicable.
   * On success, we request a reload of the category-configuration-inherited store (read-only store).
   */
  categoryApplicationLinkUpdatedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryApplicationViewActions.applicationConfigurationsUpdated),
      mergeMap((action) => {
        const categoryType = action.categoryType;
        const operation = compareApplications(action);

        if (operation.length === 0) {
          return EMPTY;
        }

        return safeMap(
          this.categoryApplicationService.updateAllApplicationsForCategory({
            categoryType,
            categoryId: action.id,
            operation,
          }),
          () =>
            CategoryConfigurationInheritedApiActions.categoryConfigurationInheritedRequested({
              categoryId: action.id,
            }),
          (error) =>
            ApplicationsApiActions.categoryApplicationConfigurationsUpdatedError({
              id: action.id,
              categoryType: action.categoryType,
              error,
            })
        );
      })
    );
  });

  /**
   * The applications couldn't be updated.
   * Effect: we request a reload of the category-configuration-inherited store (read-only store) from the api.
   */
  categoryApplicationsUpdatedErrorEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ApplicationsApiActions.categoryApplicationConfigurationsUpdatedError),
      map((action) =>
        CategoryConfigurationInheritedApiActions.categoryConfigurationInheritedRequested({
          categoryId: action.id,
        })
      )
    );
  });
}
